import type { AuditTargetType } from '@planuze/admin-contracts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@planuze/ui';
import { useAdminLocale } from '../../content/locale-provider.js';
import { useAuditTrailContent } from '../../content/sections/audit-trail.js';
import { SegmentedControl, type SegmentOption } from '../filters/segmented-control.js';
import { SearchInput } from '../search-input.js';
import { auditActionOptions, auditTargetTypeOptions } from './audit-action-catalog.js';

/* Toolbar da trilha de auditoria (ADR-0702/0704/0708). Todos os filtros são server-side:
   - Busca livre `q` (SearchInput) → ILIKE "contém" sobre `action` + `targetId`;
   - ID do operador (SearchInput) → igualdade exata; só p/ super_admin/readonly, pois
     support/billing_ops são self-scoped (o servidor força o actorId à sessão — ADR-0346);
   - Tipo de alvo + Ação (Select de escolha única) → igualdade exata (`'all'` = sem filtro);
   - Período (SegmentedControl) → janela `from`.
   O narrow de tipo/ação era faceta client-side (ADR-0704); agora vai à query (Select single). */

export type AuditPeriod = 'all' | '24h' | '7d' | '30d';

/* `'all'` = sentinel de "sem filtro" nos Selects de tipo/ação (Radix não aceita item de
   valor vazio); o call site converte para `undefined` na query server-side. `targetId` é o
   escopo por alvo do deep-link (detalhe de user/empresa → ADR-0713); não tem controle na
   toolbar (é seedado da URL e exibido como chip removível), mas viaja no spread do onChange. */
export interface AuditFiltersValue {
  readonly q: string;
  readonly actorId: string;
  readonly period: AuditPeriod;
  readonly targetType: AuditTargetType | 'all';
  readonly targetId: string;
  readonly action: string;
}

/** Estado inicial dos filtros (resolvido em runtime, não em escopo de módulo). */
export function defaultAuditFilters(): AuditFiltersValue {
  return { q: '', actorId: '', period: 'all', targetType: 'all', targetId: '', action: 'all' };
}

/* ms/hora e ms/dia como expressão (não literal) — fora do alcance do gate de magic-numbers e sem
   business value rotacionável (molde de `metrics-filters.tsx`). */
const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

const PERIOD_OFFSETS: Readonly<Record<Exclude<AuditPeriod, 'all'>, number>> = {
  '24h': 24 * MS_PER_HOUR,
  '7d': 7 * MS_PER_DAY,
  '30d': 30 * MS_PER_DAY,
};

/** Converte o período em janela `from` (ISO). `all` → sem limite inferior. */
export function periodToRange(period: AuditPeriod): { readonly from?: string } {
  if (period === 'all') return {};
  return { from: new Date(Date.now() - PERIOD_OFFSETS[period]).toISOString() };
}

interface Props {
  readonly value: AuditFiltersValue;
  readonly onChange: (value: AuditFiltersValue) => void;
  /** super_admin/readonly veem o filtro de operador; self-scoped (support/billing_ops) não. */
  readonly showActorFilter: boolean;
}

export function AuditFilters({ value, onChange, showActorFilter }: Props) {
  const { locale } = useAdminLocale();
  const copy = useAuditTrailContent().filters;
  const targetTypeOptions = auditTargetTypeOptions(locale);
  const actionOptions = auditActionOptions(locale);
  const periodOptions: readonly SegmentOption<AuditPeriod>[] = [
    { value: 'all', label: copy.periodAll },
    { value: '24h', label: copy.period24h },
    { value: '7d', label: copy.period7d },
    { value: '30d', label: copy.period30d },
  ];
  return (
    <div className="flex flex-wrap items-center gap-3">
      <SearchInput
        value={value.q}
        onChange={(q) => onChange({ ...value, q })}
        ariaLabel={copy.searchAriaLabel}
        placeholder={copy.searchPlaceholder}
      />
      {showActorFilter && (
        <SearchInput
          value={value.actorId}
          onChange={(actorId) => onChange({ ...value, actorId })}
          ariaLabel={copy.actorSearchAriaLabel}
          placeholder={copy.actorSearchPlaceholder}
        />
      )}
      <Select
        value={value.targetType}
        onValueChange={(targetType) =>
          onChange({ ...value, targetType: targetType as AuditTargetType | 'all' })
        }
      >
        <SelectTrigger className="w-48" aria-label={copy.targetTypeAriaLabel}>
          <SelectValue placeholder={copy.targetTypePlaceholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{copy.allTargetTypes}</SelectItem>
          {targetTypeOptions.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={value.action} onValueChange={(action) => onChange({ ...value, action })}>
        <SelectTrigger className="w-56" aria-label={copy.actionAriaLabel}>
          <SelectValue placeholder={copy.actionPlaceholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{copy.allActions}</SelectItem>
          {actionOptions.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <SegmentedControl<AuditPeriod>
        value={value.period}
        onChange={(period) => onChange({ ...value, period })}
        options={periodOptions}
        ariaLabel={copy.periodAriaLabel}
      />
    </div>
  );
}
